import { minBy, maxBy } from "lodash";
import { mat2d, vec2, glMatrix } from "gl-matrix";

glMatrix.setMatrixArrayType(Array);

/**
 * Element size and position attributes.
 * @typedef element
 * @type {object}
 * @property {number} x - x position of the element
 * @property {number} y - y position of the element
 * @property {number} w - width of the element
 * @property {number} h - height of the element
 * @property {number} a - angle of the element
 */

/**
 * Creates a JSON string representation of the x and y snap points for a given element.
 * @param {element} element - the element to find the snap points for.
 * @returns {string} - The JSON string representation of the snap points.
 */

const createSnapPoints = ({ x, y, h, w, a }) => {
  let snapPoints = { x: [], y: [] };
  let cornerPoints = [];

  const centerPoint = vec2.fromValues(x + w / 2, y + h / 2); // center
  cornerPoints.push(vec2.fromValues(x, y)); // top-left
  cornerPoints.push(vec2.fromValues(x + w, y)); // top-right
  cornerPoints.push(vec2.fromValues(x + w, y + h)); // bottom-right
  cornerPoints.push(vec2.fromValues(x, y + h)); // bottom-left

  // always add the center point x and y
  snapPoints.x.push([...centerPoint, centerPoint[1]]);
  snapPoints.y.push([centerPoint[1], centerPoint[0], centerPoint[0]]);

  // If there is no rotation
  if (a === undefined || a === 0) {
    snapPoints.y.push([cornerPoints[0][1], cornerPoints[0][0], cornerPoints[1][0]]);
    snapPoints.y.push([cornerPoints[3][1], cornerPoints[3][0], cornerPoints[2][0]]);
    snapPoints.x.push([...cornerPoints[0], cornerPoints[3][1]]);
    snapPoints.x.push([...cornerPoints[1], cornerPoints[2][1]]);
  } else {
    // find the min and max for both axis
    let rotateMatrix = mat2d.create();
    mat2d.translate(rotateMatrix, rotateMatrix, vec2.fromValues(x + w / 2, y + h / 2));
    mat2d.rotate(rotateMatrix, rotateMatrix, (Math.PI / 180) * a);
    mat2d.translate(
      rotateMatrix,
      rotateMatrix,
      vec2.fromValues(-1 * (x + w / 2), -1 * (y + h / 2))
    );
    for (let i = 0; i < 4; i++) {
      vec2.transformMat2d(cornerPoints[i], cornerPoints[i], rotateMatrix);
    }
    const minX = minBy(cornerPoints, (vec) => vec[0]);
    const maxX = maxBy(cornerPoints, (vec) => vec[0]);
    const minY = minBy(cornerPoints, (vec) => vec[1]);
    const maxY = maxBy(cornerPoints, (vec) => vec[1]);

    snapPoints.x.push([...minX, minX[1]]);
    snapPoints.x.push([...maxX, maxX[1]]);
    snapPoints.y.push([minY[1], minY[0], minY[0]]);
    snapPoints.y.push([maxY[1], maxY[0], maxY[0]]);
  }
  return JSON.stringify(snapPoints);
};

export default createSnapPoints;
